import {
    Color3,
    Color4,
    CustomParticleEmitter,
    Mesh,
    ParticleSystem,
    Texture,
    Vector3,
    type AbstractMesh,
    type Bone,
    type Scene,
} from '@babylonjs/core';
import { getParticleSprite, type ParticleSprite } from './particle-sprites';

/** Motes rising off a body, while something acts on it (Instinct, a rally). */
export interface EssenceMist {
    /** A steady stream, rising as thickly as `intensity`, 0 (none) to 1 (full). Already risen motes keep drifting up and fading. */
    setIntensity(intensity: number): void;
    /** A puff of `count` motes off the body at once (a rally's). A mist either streams or puffs: this stops any stream. */
    burst(count: number): void;
    /** Follows the body as it moves and animates: call once a frame. */
    update(): void;
    /** Stops it rising; what has risen fades out on its own, then it is cleaned up. */
    dispose(): void;
}

export interface EssenceMistOptions {
    /** Motes a second at full intensity. */
    readonly rate?: number;
}

/**
 * A soft, four-pointed star of light, recolored white with its shape in
 * alpha, at 128 px (Kenney's Particle Pack, CC0; docs/asset-credits.md).
 */
const MOTE_SPRITE: ParticleSprite = { family: 'star', index: 8 };

/** Motes a second at full intensity, by default: a light, steady stream. */
const DEFAULT_RATE = 14;
/** The most motes alive at once. */
const CAPACITY = 50;
/** Points on the body motes leave from, re-read every few frames so they follow its animation. */
const BODY_POINTS = 64;
const RESAMPLE_EVERY_FRAMES = 6;
/**
 * Motes leave from along the skeleton's bones, this far out from each (about
 * the body's surface: meters), so only the body gives them off, never a
 * weapon or other gear (which have no bones). Bones shorter than this (finger
 * tips, end markers) or longer (a root reaching down to the floor) are
 * skipped, as are attachment sockets, which carry no skin.
 */
const BODY_RADIUS = 0.07;
const SHORTEST_BONE = 0.03;
const LONGEST_BONE = 0.6;
const SOCKET_BONE = /^(Palm|GripAxis|GripAxisEnd)\./;
/** With no skeleton (a stand-in capsule), motes leave from the mesh's surface wherever it faces out or up instead. */
const LOWEST_NORMAL_Y = -0.2;
const UPWARD_ATTEMPTS = 6;
/** Motes lean a little outward from the body as they rise, and drift a little sideways. */
const OUTWARD = 0.2;
const SIDEWAYS_JITTER = 0.1;
/** How long a mote lives, and how fast it rises (meters a second): slowly up and away. */
const MIN_LIFE_SECONDS = 1.2;
const MAX_LIFE_SECONDS = 1.8;
const MIN_RISE = 0.22;
const MAX_RISE = 0.38;
/** A mote's size where it leaves the body (meters), and how much it slowly grows by the end. */
const MIN_SIZE = 0.004375;
const MAX_SIZE = 0.0075;
const GROWTH = 2.4;
/** It spins slowly as it rises, either way, up to this fast (radians a second). */
const MAX_SPIN = 1.4;
/** Each mote's opacity at its brightest, somewhere in this range, so some are bare traces and some show. */
const FAINTEST_ALPHA = 0.12;
const STRONGEST_ALPHA = 0.38;
/** How far the color is washed toward white: pale, tinted. */
const PALENESS = 0.4;

/** The mote texture, the scene's shared particle sprite. */
function getMoteTexture(scene: Scene): Texture {
    return getParticleSprite(scene, MOTE_SPRITE);
}

/** Starts loading the mote texture, so a brief puff (a rally's) is not over before it arrives. */
export function preloadEssenceMist(scene: Scene): void {
    getMoteTexture(scene);
}

/** A point motes leave from, in world space, and the way out from the body there. */
interface BodyPoint {
    readonly position: Vector3;
    readonly normal: Vector3;
}

/** A bone motes may leave from: it and its parent, which bound it. */
interface BoneSpan {
    readonly bone: Bone;
    readonly parent: Bone;
}

/** The body's skeleton, and the mesh it is skinned to, from the first body mesh that has one. */
function findSkeleton(body: readonly AbstractMesh[]): { readonly mesh: AbstractMesh; readonly spans: readonly BoneSpan[] } | null {
    const mesh = body.find((candidate) => candidate.skeleton);
    if (!mesh?.skeleton) {
        return null;
    }
    const spans = mesh.skeleton.bones.flatMap((bone): BoneSpan[] => {
        const parent = bone.getParent();
        // A root's own span reaches down to the floor; sockets carry no skin.
        return parent && parent.getParent() && !SOCKET_BONE.test(bone.name) ? [{ bone, parent }] : [];
    });
    return { mesh, spans };
}

const boneStart = new Vector3();
const boneEnd = new Vector3();

/**
 * Fills `points` along the skeleton's bones as the body is posed now, each
 * bone by its share of their length, each point a body's width out from the
 * bone in a random direction across it.
 */
function sampleBones(skeleton: NonNullable<ReturnType<typeof findSkeleton>>, points: BodyPoint[]): void {
    const spans: { readonly start: Vector3; readonly end: Vector3; readonly length: number }[] = [];
    let total = 0;
    for (const { bone, parent } of skeleton.spans) {
        parent.getAbsolutePositionToRef(skeleton.mesh, boneStart);
        bone.getAbsolutePositionToRef(skeleton.mesh, boneEnd);
        const length = Vector3.Distance(boneStart, boneEnd);
        if (length >= SHORTEST_BONE && length <= LONGEST_BONE) {
            spans.push({ start: boneStart.clone(), end: boneEnd.clone(), length });
            total += length;
        }
    }
    if (total === 0) {
        return;
    }
    const along = new Vector3();
    const across = new Vector3();
    for (const point of points) {
        let pick = Math.random() * total;
        const span = spans.find((candidate) => (pick -= candidate.length) <= 0) ?? spans[spans.length - 1]!;
        span.end.subtractToRef(span.start, along).normalize();
        // A random direction across the bone: any direction, less its share along the bone.
        across.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5);
        across.subtractInPlace(along.scale(Vector3.Dot(across, along)));
        if (across.lengthSquared() < 1e-6) {
            across.set(1, 0, 0);
        }
        across.normalize();
        Vector3.LerpToRef(span.start, span.end, Math.random(), point.position);
        point.position.addInPlace(across.scale(BODY_RADIUS));
        point.normal.copyFrom(across);
    }
}

/**
 * Fills `points` over the surface of every mesh with geometry in `body`
 * wherever it faces out or up, as posed now (skinned on the CPU): for a body
 * with no skeleton to follow.
 */
function sampleSurface(body: readonly AbstractMesh[], points: BodyPoint[]): void {
    const meshes = body.filter((mesh) => mesh instanceof Mesh && mesh.getTotalVertices() > 0 && mesh.isEnabled() && mesh.isVisible);
    const total = meshes.reduce((sum, mesh) => sum + mesh.getTotalVertices(), 0);
    if (total === 0) {
        return;
    }
    let filled = 0;
    meshes.forEach((mesh, index) => {
        const share = index === meshes.length - 1 ? points.length - filled : Math.round((points.length * mesh.getTotalVertices()) / total);
        const positions = mesh.getPositionData(true, true);
        const normals = mesh.getNormalsData(true, true);
        if (!positions || share <= 0) {
            return;
        }
        const world = mesh.computeWorldMatrix(true);
        const count = positions.length / 3;
        for (let pick = 0; pick < share && filled < points.length; pick++) {
            const point = points[filled++]!;
            // Preferring skin that faces out or up, judged in the world (the mesh may be turned); any, after a few tries.
            let vertex = 0;
            for (let attempt = 0; attempt < UPWARD_ATTEMPTS; attempt++) {
                vertex = Math.floor(Math.random() * count) * 3;
                if (!normals) {
                    point.normal.set(0, 0, 0);
                    break;
                }
                Vector3.TransformNormalFromFloatsToRef(normals[vertex]!, normals[vertex + 1]!, normals[vertex + 2]!, world, point.normal);
                point.normal.normalize();
                if (point.normal.y >= LOWEST_NORMAL_Y) {
                    break;
                }
            }
            Vector3.TransformCoordinatesFromFloatsToRef(positions[vertex]!, positions[vertex + 1]!, positions[vertex + 2]!, world, point.position);
        }
    });
}

/**
 * Motes rising off a body (docs/gameplay/combat-vfx.md, "Shared pieces"):
 * small, soft stars of light (`star_08.png`), pale in `color`, leave from
 * along the body's bones (only the body: never a weapon), and each rises
 * slowly, grows, spins and fades, at its own strength. They are left in the
 * world as they rise, so a moving body leaves a short trail of them. Drawn
 * with ordinary blending, never additive. Starts with none rising
 * (intensity 0).
 */
export function createEssenceMist(scene: Scene, body: readonly AbstractMesh[], name: string, color: Color3, options: EssenceMistOptions = {}): EssenceMist {
    const rate = options.rate ?? DEFAULT_RATE;
    const points: BodyPoint[] = Array.from({ length: BODY_POINTS }, () => ({ position: new Vector3(), normal: new Vector3() }));
    const skeleton = findSkeleton(body);
    const sample = (): void => (skeleton ? sampleBones(skeleton, points) : sampleSurface(body, points));
    sample();
    let frame = 0;
    /** The point the mote being made leaves from: its position is made first, then its direction from the same point. */
    let leaving: BodyPoint = points[0]!;

    const emitter = new CustomParticleEmitter();
    emitter.particlePositionGenerator = (_index, _particle, out): void => {
        leaving = points[Math.floor(Math.random() * points.length)]!;
        out.copyFrom(leaving.position);
    };
    emitter.particleDirectionGenerator = (_index, _particle, out): void => {
        out.set(
            leaving.normal.x * OUTWARD + (Math.random() - 0.5) * SIDEWAYS_JITTER,
            1,
            leaving.normal.z * OUTWARD + (Math.random() - 0.5) * SIDEWAYS_JITTER,
        );
    };

    const mist = new ParticleSystem(name, CAPACITY, scene);
    mist.particleTexture = getMoteTexture(scene);
    // The body points are already in world space.
    mist.emitter = Vector3.Zero();
    mist.particleEmitterType = emitter;
    mist.emitRate = 0;
    mist.minLifeTime = MIN_LIFE_SECONDS;
    mist.maxLifeTime = MAX_LIFE_SECONDS;
    mist.minEmitPower = MIN_RISE;
    mist.maxEmitPower = MAX_RISE;
    mist.minSize = MIN_SIZE;
    mist.maxSize = MAX_SIZE;
    mist.addSizeGradient(0, 1);
    mist.addSizeGradient(1, GROWTH);
    // Each starts at its own angle and spins its own way, at its own speed up to MAX_SPIN.
    mist.minInitialRotation = 0;
    mist.maxInitialRotation = Math.PI * 2;
    mist.minAngularSpeed = -MAX_SPIN;
    mist.maxAngularSpeed = MAX_SPIN;
    mist.gravity = Vector3.Zero();
    // Pale; each fades in as it leaves the body, peaks somewhere between faint and clear, and fades away.
    const pale = Color3.Lerp(color, Color3.White(), PALENESS);
    const at = (alpha: number): Color4 => new Color4(pale.r, pale.g, pale.b, alpha);
    mist.addColorGradient(0, at(0));
    mist.addColorGradient(0.2, at(FAINTEST_ALPHA), at(STRONGEST_ALPHA));
    mist.addColorGradient(0.6, at(FAINTEST_ALPHA * 0.6), at(STRONGEST_ALPHA * 0.5));
    mist.addColorGradient(1, at(0));
    mist.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    mist.start();

    return {
        setIntensity: (intensity: number): void => {
            // Back to streaming: while `manualEmitCount` is set, Babylon ignores the rate.
            mist.manualEmitCount = -1;
            mist.emitRate = rate * Math.max(0, intensity);
        },
        burst: (count: number): void => {
            mist.manualEmitCount = Math.max(0, mist.manualEmitCount) + count;
        },
        update: (): void => {
            frame += 1;
            if (frame % RESAMPLE_EVERY_FRAMES === 0) {
                sample();
            }
        },
        dispose: (): void => {
            // Once the last mote has faded, after the frame that found it gone (not mid-animation). Not
            // `disposeOnStop`: that disposes the textures too, and every mist in the scene shares them.
            mist.onAnimationEnd = (): void => {
                scene.onAfterRenderObservable.addOnce(() => mist.dispose(false));
            };
            mist.stop();
        },
    };
}
